import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CircleAlert, Landmark, X } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { CashShiftSummary } from "@apartos/contracts";
import { useDialog } from "../hooks/useDialog";
import { ModalPortal } from "./ModalPortal";
import { api } from "../lib/api";
import { formatUsd, moneyInput, roundMoney } from "../lib/money";

export function CashShiftModal({ shift, onClose }: { shift?: CashShiftSummary; onClose: () => void }) {
  const dialogRef = useDialog<HTMLElement>(onClose);
  const client = useQueryClient();
  const [balance, setBalance] = useState(shift ? moneyInput(shift.expectedBalance) : "");
  const [notes, setNotes] = useState("");
  const mutation = useMutation({
    mutationFn: () => shift
      ? api.closeCashShift(shift.id, { actualBalance: roundMoney(Number(balance)), notes })
      : api.openCashShift({ openingBalance: roundMoney(Number(balance)), notes }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: ["cash-shifts"] }),
        client.invalidateQueries({ queryKey: ["payments"] }),
      ]);
      onClose();
    },
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    mutation.mutate();
  };
  const numericBalance = Number(balance);
  const validBalance = balance.trim() !== "" && Number.isFinite(numericBalance) && numericBalance >= 0;
  const difference = shift && validBalance ? roundMoney(numericBalance - shift.expectedBalance) : undefined;
  return <ModalPortal onClose={onClose}>
    <section ref={dialogRef} className="booking-modal cash-shift-modal" role="dialog" aria-modal="true" aria-labelledby="cash-shift-title">
      <header className="modal-header"><div><h2 id="cash-shift-title">{shift ? "Сверить и закрыть кассу" : "Открыть кассу"}</h2><p>{shift ? "Пересчитайте наличные и зафиксируйте расхождение" : "Укажите наличные, которые физически лежат в кассе"}</p></div><button className="icon-button" onClick={onClose} aria-label="Закрыть"><X size={17} /></button></header>
      <form onSubmit={submit}><div className="modal-body">
        <div className="cash-modal-total"><Landmark size={19} /><div><span>{shift ? "По данным CRM должно быть" : "Начальный остаток наличных"}</span><strong>{shift ? formatUsd(shift.expectedBalance) : validBalance ? formatUsd(numericBalance) : "$0"}</strong></div></div>
        {shift && <div className="cash-modal-breakdown">
          <span>Начальный остаток <b>{formatUsd(shift.openingBalance)}</b></span>
          <span>Наличные оплаты <b className="is-positive">+{formatUsd(shift.cashIncome ?? 0)}</b></span>
          <span>Возвраты наличными <b className="is-negative">−{formatUsd(shift.cashRefunds ?? 0)}</b></span>
          <span>Расходы <b className="is-negative">−{formatUsd(shift.cashExpenses ?? 0)}</b></span>
        </div>}
        <div className="form-grid">
          <label className="field"><span>{shift ? "Фактически пересчитано, USD" : "Начальный остаток, USD"}</span><input name="cashBalance" required inputMode="decimal" type="number" min="0" step="0.01" value={balance} onChange={(event) => setBalance(event.target.value)} placeholder="0.00" /></label>
          <label className="field field--wide"><span>Комментарий</span><textarea name="cashShiftNotes" value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        </div>
        {shift && difference !== undefined && <div className={`cash-difference ${Math.abs(difference) < 0.005 ? "is-ok" : ""}`}><span>{Math.abs(difference) < 0.005 ? "Касса сходится" : "Расхождение"}</span><strong>{difference >= 0 ? "+" : "−"}{formatUsd(Math.abs(difference))}</strong></div>}
        {mutation.error && <div className="form-error" role="alert"><CircleAlert size={15} />{mutation.error.message}</div>}
      </div><footer className="modal-footer"><button type="button" className="secondary-button" onClick={onClose}>Отмена</button><button className="primary-button" disabled={mutation.isPending || !validBalance}>{mutation.isPending ? "Сохраняем…" : shift ? "Закрыть кассу" : "Открыть кассу"}</button></footer></form>
    </section>
  </ModalPortal>;
}
